import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { PrimaryButton, IconNavButton } from './touchable';
import { updateSession, savePattern } from '../storage/persistence';
import { deriveColor } from '../logic/patternColor';
import { generateDsl } from '../logic/dsl-generator';
import { PatternParamsCard } from './PatternParamsCard';
import { ProjectDetailsForm } from './ProjectDetailsForm';
import { defaultsForAll } from './paramHelpers';
import type { Session } from '../types/session';
import type { Pattern } from '../types/state';
import type { IRValue } from '../types/ir';

interface NewProjectWizardProps {
  onCancel: () => void;
  onProjectCreated: (sessionId: string) => void;
  /**
   * The pattern to start a project from. Chosen in the Pattern Library (which
   * is now the single pattern-picking surface), so it is always provided.
   */
  pattern: Pattern;
}

export const NewProjectWizard: React.FC<NewProjectWizardProps> = ({
  onCancel,
  onProjectCreated,
  pattern,
}) => {
  const { t } = useTranslation('session');
  const [details, setDetails] = useState<{
    title: string;
    metadata: Record<string, string>;
    color: string;
  }>({
    title: pattern.title,
    metadata: pattern.structure?.metadata ? { ...pattern.structure.metadata } : {},
    color: '',
  });
  const [detailsValid, setDetailsValid] = useState(true);
  const [paramValues, setParamValues] = useState<Record<string, IRValue>>(
    defaultsForAll(pattern.structure?.params ?? []),
  );
  const [paramsValid, setParamsValid] = useState(true);

  const handleStart = async () => {
    if (!detailsValid || !paramsValid) return;
    const sessionId = crypto.randomUUID();
    const now = new Date().toISOString();
    const params = pattern.structure?.params ?? [];
    let compiledDsl: string | undefined;
    if (pattern.js_source) {
      const defaults = defaultsForAll(pattern.structure?.params ?? []);
      const isNonDefault = Object.keys(paramValues).some((k) => paramValues[k] !== defaults[k]);
      if (isNonDefault) {
        try {
          // Dynamic import keeps the JS pattern compiler (~3 KB gz) out of
          // the eager bundle. Only loaded when a user actually starts a
          // project from a JS-authored pattern with non-default params.
          const { executeJSPattern } = await import('../logic/js-authoring');
          const recompiledIR = await executeJSPattern(pattern.js_source, paramValues);
          compiledDsl = generateDsl(recompiledIR);
        } catch (err) {
          alert(
            t(
              'newProject.errors.compileFailed',
              'Failed to compile pattern with chosen parameters: {{message}}',
              {
                message: err instanceof Error ? err.message : String(err),
              },
            ),
          );
          return;
        }
      }
    }
    const newSession: Session = {
      id: sessionId,
      patternId: pattern.id,
      title: details.title.trim(),
      status: 'idle',
      startedAt: now,
      updatedAt: now,
      snapshot: { lastEventIndex: -1, currentRow: 0, totalTimeMs: 0 },
      history: [],
      ...(params.length > 0 ? { paramValues } : {}),
      ...(compiledDsl ? { compiledDsl } : {}),
      ...(Object.keys(details.metadata).length > 0 ? { metadata: details.metadata } : {}),
    };
    // Only persist when the user picked a color that differs from the pattern's
    // effective color (explicit `pattern.color` if set, else the derived default).
    // Without this guard, every first project start would write the derived color
    // back to the pattern even when the user never touched the picker.
    const effectiveExisting = pattern.color ?? deriveColor(pattern.id);
    if (details.color && details.color !== effectiveExisting) {
      await savePattern({ ...pattern, color: details.color });
    }
    await updateSession(newSession);
    onProjectCreated(sessionId);
  };

  return (
    <div className="fixed inset-0 bg-base z-50 flex flex-col transition-colors duration-300">
      <header className="p-6 bg-card border-b border-line sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <IconNavButton
            onClick={onCancel}
            aria-label={t('common:actions.back', 'Back')}
            className="-ml-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </IconNavButton>
          <h2 className="text-2xl font-black font-display text-ink tracking-tight">
            {t('newProject.nameYourProject', 'Name your project')}
          </h2>
        </div>
      </header>

      <div className="flex-grow overflow-y-auto p-6 max-w-4xl mx-auto w-full">
        <ProjectDetailsForm
          title={details.title}
          metadata={pattern.structure?.metadata}
          patternId={pattern.id}
          initialColor={pattern.color}
          onChange={setDetails}
          onValidityChange={setDetailsValid}
        />
        <p className="text-xs text-ink-faint mt-2">
          {t('newProject.patternLabel', 'Pattern: {{title}}', { title: pattern.title })}
        </p>
        {pattern.structure && pattern.structure.params.length > 0 && (
          <div className="mt-4">
            <PatternParamsCard
              params={pattern.structure.params}
              values={paramValues}
              onChange={setParamValues}
              onValidityChange={setParamsValid}
            />
          </div>
        )}
      </div>

      <div className="p-6 border-t border-line">
        <PrimaryButton
          onClick={handleStart}
          disabled={!detailsValid || !paramsValid}
          className="py-4 rounded-2xl"
        >
          {t('newProject.actions.knit', 'Knit')}
        </PrimaryButton>
      </div>
    </div>
  );
};
